/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 17/11/21
 */
import React, {memo, useCallback, useEffect, useMemo} from 'react';
import {Button, Form, Input} from 'antd';
import {useTranslation} from 'react-i18next';
import {useDialogsContext} from '@/contexts/DialogsContext';
import FloatContainer, {TYPE_CONTAINER} from '@/components/FloatContainer';
import {useResponsive} from '@/contexts/ResponsiveContext';
import style from './index.less';
import {Typography} from 'antd';
import useCrud, {showConfirm} from '@/hooks/useCrud';
import QuestionnaireApiService from '@/modules/templates/services/QuestionnaireApiService';
import {CACHE_KEY_AUDITOR_AUDITS} from '@/modules/auditorAudits/contants/auditorAudits';
import Editor from '@/components/Editor';
import QuestionnairePicturesWall from '@/modules/templates/components/QuestionnairePicturesWall';

const { Text } = Typography;


export const QUESTIONNAIRE_DIALOG = 'questionnaire-dialog';

const QuestionnaireDialog = () => {
  const {t} = useTranslation('templates');
  const {isOpen, closeDialog, payload } = useDialogsContext(QUESTIONNAIRE_DIALOG);
  const {isMobile} = useResponsive();
  const { update, isUpdating } = useCrud(QuestionnaireApiService, [CACHE_KEY_AUDITOR_AUDITS, 'get-one-questionnaire']);
  const [form] = Form.useForm();
  const questions = useMemo(() => {
    return payload?.questionnaire?.questionsAnswers || [];
  }, [payload?.questionnaire?.questionsAnswers]);

  const questionType = useCallback((question, type) => {
    if (type === 'SIMPLE' || type === 'PARAGRAPH') {
      return type;
    }
    return question?.[type]?.answerType;
  }, []);

  useEffect(() => {
    isOpen && form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    const questionsAnswers = form?.getFieldValue('questionsAnswers');
    await update({
      _id: payload?.questionnaire?._id,
      questionsAnswers,
    });
    closeDialog();
  }, [form, update, payload?.questionnaire?._id, closeDialog]);

  return (
    <FloatContainer
      type={isMobile ? TYPE_CONTAINER.DRAWER : TYPE_CONTAINER.MODAL}
      visible={isOpen}
      title={t('questionnaireIE.create')}
      onClose={closeDialog}
      customClass={style.modalDivider}
      width={720}
      footer={[
        <Button className="mx-2" key="back" onClick={closeDialog} disabled={isUpdating}>
          {t('cancel')}
        </Button>,
        <Button className="mx-2"
          key="submit" type="primary"
          onClick={() => showConfirm(t, '', () => handleSubmit(), 'questionnaireIE.saveAndSendMsg')}
          loading={isUpdating}>
          {t('questionnaireIE.saveAndSend')}
        </Button>,
      ]}>
      <Form form={form} onFinish={handleSubmit} layout="vertical" className="custom-form" initialValues={payload?.questionnaire?.questionsAnswers}>
        <Text type={payload?.questionnaire?.sent ? 'success' : 'danger'} className='mb-2'>{payload?.questionnaire?.sent ? t('questionnaireIE.sent') : t('questionnaireIE.notSent')}</Text>
        <Form.List name={'questionsAnswers'} initialValue={questions}>
          {(fields) => (
            <div>
              {fields.map((field) => (
                <>
                  <Form.Item
                    {...field}
                    name={[field?.name, 'sectionTitle']}
                    key={field?.name}
                    className='mb-1'
                  >
                    <div style={{
                      backgroundColor: 'var(--primary-color)',
                      padding: '0.5rem 1rem',
                      margin: '0.5rem 0',
                      boxShadow: '0 1px 0 #E4E4E4',
                      borderRadius: '6px',
                      height: '50px',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      fontSize: '1.15rem',
                    }}>
                      <Text>{questions?.[field?.fieldKey]?.sectionTitle || ''}</Text>
                    </div>
                  </Form.Item>
                  <Form.List name={[field?.name,'questionsAnswers']} initialValue={questions?.[field?.fieldKey]?.questionsAnswers}>
                    {(iFields) => (
                      <div>
                        {iFields.map((iField) => (
                          <>
                            <Form.Item
                              {...iField}
                              name={[iField?.name, 'question']}
                              key={[iField?.key, 'question']}
                              className='mb-1'
                            >
                              <Text>{questions?.[field?.fieldKey]?.questionsAnswers?.[iField?.fieldKey]?.name || questions?.[field?.fieldKey]?.questionsAnswers?.[iField?.fieldKey]?.question}</Text>
                            </Form.Item>
                            <Form.Item
                              {...iField}
                              name={[iField?.name, 'answer']}
                              key={[iField?.key, 'answer']}
                              rules={[{ required: true }]}
                              initialValue={questions?.[field?.fieldKey]?.questionsAnswers?.[iField?.fieldKey]?.answer || ''}
                            >
                              {questionType(questions?.[field?.fieldKey]?.questionsAnswers?.[iField?.fieldKey], questions?.[field?.fieldKey]?.questionsAnswers?.[iField?.fieldKey]?.type) === 'PARAGRAPH'
                                ? <Editor/> : <Input/>}
                            </Form.Item>
                            <Form.Item
                              {...iField}
                              name={[iField?.name, 'type']}
                              key={[iField?.key, 'type']}
                              initialValue={questions?.[field?.fieldKey]?.questionsAnswers?.[iField?.fieldKey]?.type || 'SIMPLE'}
                              hidden
                            >
                              <Input/>
                            </Form.Item>
                          </>
                        ))}
                      </div>
                    )}
                  </Form.List>
                </>
              ))}
            </div>
          )}
        </Form.List>
      </Form>
      <div className="mt-6">
        <QuestionnairePicturesWall translation={'templates'} questionnaire={payload?.questionnaire}/>
      </div>
    </FloatContainer>
  );
};

export default memo(QuestionnaireDialog);

